/**
 * useAutoRefresh — reusable periodic data-refresh hook.
 *
 * Schedules a repeating interval that calls `fetchFn` every `intervalMs` ms.
 * Skips ticks when the browser tab is hidden (Page Visibility API). Tracks
 * consecutive failures and calls `onError` with the updated count after each
 * rejected tick. Clears the interval on unmount or when `enabled` flips false.
 *
 * `fetchFn` and `onError` are stored in refs so the interval closure always
 * invokes the latest versions without restarting the timer on every render.
 *
 * Validates: Requirements 2.1–2.11.
 */

import { useEffect, useRef } from 'react';

export interface UseAutoRefreshOptions {
  /** The async operation to run on each tick. Errors are caught internally. */
  fetchFn: () => Promise<void>;
  /** When false, no interval is scheduled. */
  enabled: boolean;
  /** Milliseconds between ticks. Defaults to 2 * 60 * 1000 (2 minutes). */
  intervalMs?: number;
  /**
   * Called after each failed tick with the current consecutive-failure count.
   * The hook itself does not display any UI; the component uses this to decide
   * whether to show a stale-data banner.
   */
  onError?: (consecutiveFailures: number) => void;
}

const DEFAULT_INTERVAL_MS = 2 * 60 * 1000;

export function useAutoRefresh(options: UseAutoRefreshOptions): void {
  const { enabled, intervalMs = DEFAULT_INTERVAL_MS } = options;

  // Keep refs to the latest fetchFn and onError so the interval closure never
  // captures a stale version, without needing to restart the timer.
  const fetchFnRef = useRef<() => Promise<void>>(options.fetchFn);
  const onErrorRef = useRef<((consecutiveFailures: number) => void) | undefined>(
    options.onError
  );

  // Consecutive failure counter — stored as a ref because it drives no render.
  const consecutiveFailuresRef = useRef<number>(0);

  // Keep refs in sync with the latest props on every render.
  fetchFnRef.current = options.fetchFn;
  onErrorRef.current = options.onError;

  useEffect(() => {
    // Requirement 2.2: when disabled, do nothing.
    if (!enabled) {
      return;
    }

    // Requirement 2.3: schedule a repeating interval.
    const intervalId = setInterval(() => {
      // Requirement 2.4: skip when the tab is hidden.
      if (document.visibilityState === 'hidden') {
        return;
      }

      // Requirement 2.5: call fetchFn when visible.
      void fetchFnRef.current().then(
        () => {
          // Requirement 2.6: reset counter on success.
          consecutiveFailuresRef.current = 0;
        },
        () => {
          // Requirement 2.7: increment counter on failure, notify caller.
          consecutiveFailuresRef.current += 1;
          onErrorRef.current?.(consecutiveFailuresRef.current);
        }
      );
    }, intervalMs);

    // Requirements 2.8, 2.9, 2.11: clear on unmount, enabled→false, or intervalMs change.
    return () => {
      clearInterval(intervalId);
    };
  }, [enabled, intervalMs]);
}
